import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./style.css";
import fetchSingleBlog from "../../utilities/fetchSingleBlog";
import { PortableText } from "@portabletext/react";
import { DataContext } from "../../Context/Context";
import { client } from "../../sanityClient";
import imageUrlBuilder from "@sanity/image-url";
import { Helmet } from "react-helmet";
import { ArrowRight} from "lucide-react";

const BlogDetails = () => {
  const { blogs, setBlogs } = useContext(DataContext);
  const [blog, setBlog] = useState({});
  const [loading, setLoading] = useState(false);
  const { slug } = useParams();

  useEffect(() => {
    setLoading(true);
    const getBlog = async () => {
      try {
        const data = await fetchSingleBlog(slug);
        setBlog(data);
        setLoading(false);
      } catch (err) {
        console.error(err);
      }
    };

    if (slug) getBlog();
  }, [slug, setBlog]);

  useEffect(() => {
    client
      .fetch(
        `*[_type == "blog"] | order(publishedAt desc) {
        title,
        slug,
        author,
        publishedAt,
        mainImage {
          asset->{
            _id,
            url
          }
        },
        body,
        seoTitle,
        metaDescription,
        keywords,
        socialShareImage {
          asset->{
            _id,
            url
          }
        },
        appleTouchIcon {
          asset->{
            _id,
            url
          }
        },
        canonicalUrl
      }`
      )
      .then((data) => {
        setBlogs(data);
      })
      .catch(console.error);
  }, [setBlogs]);

  if (!blog) {
    return <p>Blog not found.</p>;
  }

  const builder = imageUrlBuilder({
    projectId: process.env.REACT_APP_SANITY_PROJECT_ID,
    dataset: process.env.REACT_APP_SANITY_DATASET,
  });

  function urlFor(source) {
    return builder.image(source);
  }

  // --- 2. Define the component for rendering images ---
  const components = {
    types: {
      image: ({ value }) => {
        if (!value?.asset?._ref) {
          return null;
        }
        return (
          <img
            src={urlFor(value).url()} // Use the builder to get the URL
            alt={value.alt || "Blog image"}
            loading="lazy"
            className="w-full rounded-lg my-6" // Added 'my-6' for vertical margin
          />
        );
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>{blog?.title || blog?.seoTitle}</title>
        <meta name="description" content={blog?.metaDescription} />

        {/* Keywords */}
        {blog?.keywords && (
          <meta name="keywords" content={blog?.keywords.join(", ")} />
        )}

        {/* Canonical URL */}
        {blog?.canonicalUrl && (
          <link rel="canonical" href={blog?.canonicalUrl} />
        )}

        {/* Social Sharing (Open Graph) */}
        <meta property="og:title" content={blog?.seoTitle || blog?.title} />
        <meta property="og:description" content={blog?.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        {blog?.socialShareImageUrl && (
          <meta property="og:image" content={blog.socialShareImageUrl} />
        )}

        {/* Twitter Card (optional) */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog?.seoTitle || blog?.title} />
        <meta name="twitter:description" content={blog?.metaDescription} />
        {blog?.socialShareImageUrl && (
          <meta name="twitter:image" content={blog?.socialShareImageUrl} />
        )}

        {/* Apple Touch Icon */}
        {blog?.appleTouchIconUrl && (
          <link rel="apple-touch-icon" href={blog?.appleTouchIconUrl} />
        )}
      </Helmet>
      <section className="pt-24 pb-10">
        <div className="container flex lg:flex-row md:flex-row flex-col items-start gap-5">
          <div className="lg:w-[70%] md:w-[60%] w-full">
            {loading ? (
              <div className="flex w-full flex-col gap-4">
                <div className="skeleton h-56 w-full"></div>
                <div className="skeleton h-4 w-28"></div>
                <div className="skeleton h-4 w-full"></div>
                <div className="skeleton h-4 w-full"></div>
              </div>
            ) : (
              <>
                {/* Blog Title */}
                {loading ? (
                  <div className="skeleton h-4 w-full"></div>
                ) : (
                  <h1 className="text-2xl font-medium text-[#74716d] mb-4">
                    {blog?.title}
                  </h1>
                )}

                {/* Blog Date */}
                <p className="text-sm text-[#74716d] mb-6">
                  {new Date(blog?.publishedAt).toDateString()}
                </p>
                <div>
                  {blog?.categories?.map((category, index) => (
                    <div className="px-2 py-1 bg-green-100 inline-block text-green-500 rounded-lg mb-4">
                      {category?.title}
                    </div>
                  ))}
                </div>
                {/* Main Image */}
                <img
                  src={blog?.mainImageUrl}
                  alt={blog?.title}
                  loading="lazy"
                  className="w-full rounded-lg mb-6"
                />

                {/* Blog Content */}
                <div
                  className="text-sm text-[#74716d] leading-relaxed"
                  id="blog_description"
                >
                  {/* {truncateDescription(blog.body, 99999)} */}
                  <PortableText value={blog?.body} components={components} />
                </div>
              </>
            )}
          </div>

          {/* Recommended Blogs */}
          <aside className=" lg:w-[30%] md:w-[40%] w-full">
            <h2 className="text-xl font-semibold text-[#74716d] mb-4">
              Recommended Blogs
            </h2>
            <div className="space-y-4">
              {blogs
                .filter((curr) => curr?.slug?.current !== blog?.slug?.current)
                ?.map((recommendedBlog, index) => (
                  <div key={index} className="flex space-x-4">
                    <img
                      src={recommendedBlog?.mainImage?.asset?.url}
                      alt={recommendedBlog?.title}
                      loading="lazy"
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div>
                      <h3 className="text-[16px] font-medium leading-[20px] text-[#74716d]">
                        {recommendedBlog?.title?.slice(0, 50)}...
                      </h3>
                      <a
                        href={`/blog/${recommendedBlog?.slug?.current}`}
                        className="text-[#74716d] text-[16px] hover:text-[#74716d] hover:underline flex items-center gap-1"
                      >
                        <span>Read More</span> <ArrowRight size={16} />
                      </a>
                    </div>
                  </div>
                ))}
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export default BlogDetails;
